"use client"

import { type UseQueryStatesKeysMap, useQueryStates, type Values } from "nuqs"
import {
  createContext,
  type PropsWithChildren,
  use,
  useCallback,
  useMemo,
  useTransition,
} from "react"
import { isDefaultState } from "~/lib/parsers"

type FiltersContextType<T extends UseQueryStatesKeysMap> = {
  filters: Values<T>
  isLoading: boolean
  isDefault: boolean
  enableSort: boolean
  enableFilters: boolean
  defaultSort?: string
  updateFilters: (values: Partial<Values<T>> | null) => void
}

const FiltersContext = createContext<FiltersContextType<any>>(null!)

export type FiltersProviderProps<T = any> = {
  schema: T
  enableSort?: boolean
  enableFilters?: boolean
  defaultSort?: string
}

const FiltersProvider = <T extends UseQueryStatesKeysMap>({
  children,
  schema,
  enableSort = true,
  enableFilters = false,
  defaultSort,
}: PropsWithChildren<FiltersProviderProps<T>>) => {
  const [isLoading, startTransition] = useTransition()

  const [filters, setFilters] = useQueryStates(schema, {
    shallow: false,
    history: "push",
    startTransition,
    limitUrlUpdates: {
      method: "debounce",
      timeMs: 250,
    },
  })

  const isDefault = isDefaultState(schema as any, filters, ["page", "perPage"])

  const updateFilters = useCallback(
    (values: Partial<Values<T>> | null) => {
      if (values === null) {
        setFilters(null)
      } else {
        // nuqs merges partial updates. Queue only changed keys, rather than
        // rescheduling every unchanged facet on each checkbox click.
        setFilters({ ...values, page: null })
      }
    },
    [setFilters],
  )

  const contextValue = useMemo(
    () => ({
      filters,
      isLoading,
      isDefault,
      updateFilters,
      enableSort,
      enableFilters,
      defaultSort,
    }),
    [filters, isLoading, isDefault, updateFilters, enableSort, enableFilters, defaultSort],
  )

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>
}

function useFilters<T extends UseQueryStatesKeysMap>(): FiltersContextType<T> {
  const context = use(FiltersContext)

  if (context === undefined) {
    throw new Error("useFilters must be used within a FiltersProvider")
  }

  return context
}

export { FiltersProvider, useFilters }
